// src/services/settingsApi.ts
// Everything the Settings page reads and saves; same endpoints as the app.
import apiClient from "./apiClient";
import { TaxOption, getOwnerTaxes, getUserTaxes } from "./taxApi";

/** The server's message for a failed request, or `fallback`. */
export const apiErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

// ── Account ────────────────────────────────────────────────────────────────

export type Me = {
  _id: string;
  name: string;
  email: string;
  tier: string;
  businessName: string | null;
  hasFinancePasscode: boolean;
  financeLockEnabled: boolean;
  workspaces: Array<{ id: string; name: string; role: string; isOwn: boolean }>;
  defaultWorkspaceId: string | null;
};

export const getMe = async (): Promise<Me> => {
  // The profile doesn't depend on the active business, so send none.
  const { data } = await apiClient.get("/auth/me", {
    headers: { "X-Workspace-Id": "" },
  });
  return data;
};

export type AccountSettings = {
  name: string;
  businessName: string;
  /** Sign-in email; read-only. */
  email: string;
  phone: string;
  address: string;
  websiteUrl: string;
  country: string | null;
  currency: string | null;
  timezone: string | null;
  marketingEmails: boolean;
};

export type AccountSettingsUpdate = Partial<Omit<AccountSettings, "email">>;

export const getAccountSettings = async (): Promise<AccountSettings> => {
  const { data } = await apiClient.get("/auth/me/settings");
  return data;
};

export const updateAccountSettings = async (
  changes: AccountSettingsUpdate
): Promise<AccountSettings> => {
  const { data } = await apiClient.patch("/auth/me/settings", changes);
  return data;
};

export const changePassword = (currentPassword: string, newPassword: string) =>
  apiClient.post("/auth/change-password", { currentPassword, newPassword });

export const archiveAccount = () => apiClient.put("/auth/archive");

// ── Preferences ────────────────────────────────────────────────────────────

export type NotificationPrefs = {
  reminderOffsetMinutes: number;
  timezone: string | null;
};

export const getNotificationPrefs = async (): Promise<NotificationPrefs> => {
  const { data } = await apiClient.get("/user-settings/me/notifications");
  return {
    reminderOffsetMinutes: Number(data?.reminderOffsetMinutes ?? 0),
    timezone: data?.timezone ?? null,
  };
};

export const updateNotificationPrefs = (prefs: NotificationPrefs) =>
  apiClient.put("/user-settings/me/notifications", prefs);

// ── Payment methods & card fee ─────────────────────────────────────────────

export type PaymentFee = {
  type: "none" | "flat" | "percent";
  value: number;
  enabled?: boolean;
  label?: string;
  taxable?: boolean;
  fixedAmount?: number;
};

export type PaymentMethod = {
  _id: string;
  paymentMethod: string;
  qrCode: string;
  fee?: PaymentFee;
};

const normalizePaymentMethods = (data: any): PaymentMethod[] => {
  const raw = data?.paymentQRs ?? data ?? [];
  if (Array.isArray(raw)) {
    return raw.map((pm: any) => ({
      _id: String(pm?._id?.$oid ?? pm?._id ?? pm?.paymentMethod ?? ""),
      paymentMethod: typeof pm?.paymentMethod === "string" ? pm.paymentMethod : "",
      qrCode: typeof pm?.qrCode === "string" ? pm.qrCode : "",
      fee: pm?.fee,
    }));
  }
  // Older accounts store { method: qrUrl }.
  if (raw && typeof raw === "object") {
    return Object.entries(raw).map(([method, url]) => ({
      _id: method,
      paymentMethod: method,
      qrCode: String(url),
    }));
  }
  return [];
};

/** The business's own methods, or the employer's when working for one. */
export const getPaymentMethods = async (
  employer: boolean
): Promise<PaymentMethod[]> => {
  const { data } = await apiClient.get(
    employer ? "/auth/owner-payment-qr" : "/auth/payments"
  );
  return normalizePaymentMethods(data);
};

const paymentForm = (method: string, qrImage?: File | null, fee?: PaymentFee) => {
  const form = new FormData();
  if (qrImage) form.append("qrCode", qrImage);
  if (method) form.append("paymentMethod", method);
  if (fee) {
    form.append("feeType", fee.type);
    form.append("feeValue", String(fee.value));
    if (fee.enabled != null) form.append("feeEnabled", fee.enabled ? "1" : "0");
    if (fee.label != null) form.append("feeLabel", fee.label);
    if (fee.taxable != null) form.append("feeTaxable", fee.taxable ? "1" : "0");
    if (fee.fixedAmount != null)
      form.append("feeFixedAmount", String(fee.fixedAmount));
  }
  return form;
};

export const addPaymentMethod = (
  method: string,
  qrImage?: File | null,
  fee?: PaymentFee
) => apiClient.post("/auth/payments", paymentForm(method, qrImage, fee));

export const updatePaymentMethod = (
  id: string,
  method: string,
  qrImage?: File | null,
  fee?: PaymentFee
) => apiClient.put(`/auth/payments/${id}`, paymentForm(method, qrImage, fee));

export const deletePaymentMethod = (id: string) =>
  apiClient.delete(`/auth/payments/${id}`);

// ── Taxes ──────────────────────────────────────────────────────────────────

export const getTaxes = (employer: boolean): Promise<TaxOption[]> =>
  employer ? getOwnerTaxes() : getUserTaxes();

export const addTax = (tax: { name: string; rate: number; isDefault?: boolean }) =>
  apiClient.post("/settings/taxes", tax);

export const updateTax = (
  id: string,
  changes: { name?: string; rate?: number; isDefault?: boolean }
) => apiClient.put(`/settings/taxes/${id}`, changes);

export const deleteTax = (id: string) => apiClient.delete(`/settings/taxes/${id}`);

// ── Square ─────────────────────────────────────────────────────────────────

export type SquareStatus = {
  connected: boolean;
  scope?: "own" | "employer";
  message?: string;
  employerName?: string;
  merchantName?: string;
  locationName?: string;
  /** Owner only: employees may use this Square account. */
  employeeAccess?: boolean;
};

export const getSquareStatus = async (): Promise<SquareStatus> => {
  const { data } = await apiClient.get("/square/status");
  return data;
};

/** Square's sign-in page; afterwards it comes back to Settings on this site. */
export const getSquareConnectUrl = async (): Promise<string> => {
  const { data } = await apiClient.get("/square/connect-url", {
    params: { returnTo: window.location.origin },
  });
  return data?.url;
};

export const disconnectSquare = () => apiClient.post("/square/disconnect");

export const setSquareEmployeeAccess = (employeeAccess: boolean) =>
  apiClient.patch("/square/settings", { employeeAccess });

// ── Team ───────────────────────────────────────────────────────────────────

export type PayBaseType = "none" | "hourly" | "per_event";
export type CommissionMode = "add" | "instead" | "higher";

/** How an employee is paid; mirrors the backend's pay settings. */
export type PayTerms = {
  baseType: PayBaseType;
  baseAmount: number;
  commissionPercent: number;
  commissionThreshold: number;
  commissionMode: CommissionMode;
};

export type Employee = {
  _id: string;
  email: string;
  name?: string;
  role: string;
  status?: "active" | "inactive";
  invitationStatus?: string;
  commission?: number;
  pay?: Partial<PayTerms>;
};

export type MyInvitations = {
  /** Pending invitations to the signed-in user's email. */
  invitations: any[];
  /** Accepted jobs, one per business the user works for. */
  employers: any[];
};

export const getEmployees = async (): Promise<Employee[]> => {
  const { data } = await apiClient.get("/settings/employees");
  return Array.isArray(data) ? data : [];
};

export const inviteEmployee = (email: string, pay: PayTerms) =>
  apiClient.post("/settings/employees/invite", {
    email,
    role: "employee",
    commission: pay.commissionPercent,
    pay,
  });

export const updateEmployee = (id: string, role: string, pay: PayTerms) =>
  apiClient.patch(`/settings/employees/${id}/role`, {
    newRole: role,
    newCommission: pay.commissionPercent,
    pay,
  });

export const removeEmployee = (id: string) =>
  apiClient.delete(`/settings/employees/${id}`);

export const reactivateEmployee = (id: string) =>
  apiClient.patch(`/settings/employees/${id}/reactivate`);

export const resendInvitation = (id: string) =>
  apiClient.post(`/settings/employee/${id}/resend-invitation`);

export const cancelInvitation = (id: string) =>
  apiClient.post(`/settings/employee/${id}/cancel-invitation`);

export const getMyInvitations = async (): Promise<MyInvitations> => {
  const { data } = await apiClient.get("/settings/my-invitations");
  return {
    invitations: Array.isArray(data?.invitations) ? data.invitations : [],
    employers: Array.isArray(data?.employers) ? data.employers : [],
  };
};

export const acceptInvitation = (token: string, userId: string) =>
  apiClient.post("/settings/employees/accept-invitation", { token, userId });

export const declineInvitation = (invitationId: string) =>
  apiClient.post("/settings/employees/decline-invitation", {
    employeeId: invitationId,
  });

export const leaveEmployer = (ownerId?: string) =>
  apiClient.post("/settings/employees/leave-owner", ownerId ? { ownerId } : {});
